import {
  VdFeatureManager,
  VdPerformanceTracking,
  VdTracking,
} from '@vend/vend.js'
import angular, { IScope } from 'angular'
import { ApiCustomer, ApiUser } from '@vend/types'
import ngRedux from 'ng-redux'
import {
  getChangeAmountForDisplay,
  getOutstandingBalance,
  getPaymentsTotal,
  getTotalQuantityByType,
  hasOutstandingBalance,
  isAccountBalancePaymentPaid,
  isCompleted,
  isConfirmedReadyToComplete,
  isLayby,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccount,
  isPartialPaidSale,
  isQuote,
  isSaleTotalGreaterThanZero,
  isSaleTotalLessThanZero,
  isSaleTotalZero,
  isSynced,
  isUnpaidSale,
  isUploadedToBeCompleted,
  isValidCustomer,
  saleIsExchange,
  shouldDisplaySaleNote,
} from '../../functions/sale'
import {
  CONFIRMED_STATUSES_CLOSED,
  GIFT_RECEIPT_STATUSES,
  SaleStatus,
  SaleSyncStatus,
} from '../../models/sale.constants'
import template from '../../components/receipts/receipt-selection-popover/receipt-selection-popover.html'
import { STATES } from '../../../../lib/routes'
import { EVENT, DebounceOptions } from '../../constants'
import {
  $log,
  $q,
  $rootScope,
  $state,
  $timeout,
  $transitions,
} from '../../ng-compat/imports'
import { RegisterManager } from '../../managers/register-manager'
import { ReceiptData, SaleManager } from '../../managers/sale-manager'
import { TrainingModeManager } from '../../managers/training-mode-manager'
import { UserManager } from '../../managers/user-manager'
import { PaymentManager } from '../../managers/payment-manager'
import { ReceiptSettingsStore } from '../../factories/database/receipt-settings-store'
import { LoyaltyManager } from '../../managers/loyalty-manager'
import { RetailerSettingsManager } from '../../managers/retailer-settings-manager'
import {
  customerAddedOnConfirmation,
  ReduxCurrentSale,
  selectCurrentSale,
  selectCurrentSaleAsSale,
  selectLineItems,
  setSaleCompletedBy,
} from '../../state/slices/currentSale/currentSaleSlice'
import { LineItem } from '../../models/line-item'
import { hasChange } from '../../functions/payments'
import { print } from '../../components/print/print'

type PrintOptions = 'print' | 'gift' | 'other'
type CheckUploadAndPrintOptions = {
  receiptId?: string
  defaultTemplate?: boolean
  selectedPrintOption?: PrintOptions
}
/**
 * =======================================
 * SALE CONFIRMATION CONTROLLER
 * =======================================
 */
export class SaleConfirmationCtrl {
  private _navigatingTo: $TSFixMe
  private _unsubscribeStateChange: $TSFixMe
  private _whenInitialised: $TSFixMe
  private whenSaleCanBeFinalised: Promise<void> = Promise.resolve()
  private selectedReceiptId?: string
  private isDefaultTemplate?: boolean
  public activePrinting?: PrintOptions
  public autoPrintEnabled?: boolean
  public clickedPrintReceiptButton: $TSFixMe
  public completeSaleLabel: $TSFixMe
  public customerErrorTemplate: $TSFixMe
  public customerSource: $TSFixMe
  public displayEmailReceipt?: boolean
  public hasMultipleReceipts?: boolean
  public hasTrackedSaleNoteUpdated: $TSFixMe
  public isCompleted: $TSFixMe
  public isConfirmedReadyToComplete: $TSFixMe
  public isFinalisingSale: $TSFixMe
  public loading = true
  public sale: ReduxCurrentSale
  public lineItems: LineItem[]
  public user: ApiUser
  public form!: any
  public debounceOptions: DebounceOptions | undefined
  public addCustomerPlaceholder: string

  constructor(
    private $scope: IScope,
    private saleManager: SaleManager,
    private vdTracking: VdTracking,
    private registerManager: RegisterManager,
    private userManager: UserManager,
    private userSwitchingService: $TSFixMe,
    public trainingModeManager: TrainingModeManager,
    public loyaltyManager: LoyaltyManager,
    private paymentManager: PaymentManager,
    private vdFeatureManager: VdFeatureManager,
    private vdEscapeStack: $TSFixMe,
    private vdPerformanceTracking: VdPerformanceTracking,
    private vdPopover: $TSFixMe,
    private customerModals: $TSFixMe,
    private customerSuggestions: $TSFixMe,
    private receiptSettingsStore: ReceiptSettingsStore,
    private retailerSettingsManager: RetailerSettingsManager,
    private $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'

    this.sale = selectCurrentSale(this.$ngRedux.getState())
    this.lineItems = selectLineItems(this.$ngRedux.getState())
    const disconnect = this.$ngRedux.connect(state => {
      return {
        sale: selectCurrentSale(state),
        lineItems: selectLineItems(state),
      }
    })(this)

    $scope.$on('$destroy', disconnect)

    this.debounceOptions = this.saleManager.debounceOptions

    // We want to allow the user to use the back button as a way of dismissing the sale confirmation screen
    this._unsubscribeStateChange = $transitions.onStart({}, event => {
      // Extra backup in-case the unsubscribe doesn't happen in time when in training mode.
      if (!this.trainingModeManager.trainingModeEnabled) {
        this.closeSaleConfirmation({
          targetRoute: event.to().name,
          routeParams: event.params('to'),
          source: 'stateChange',
        })
        // We have to cancel event to wait for the posting of the sale.
        return false
      }
    })
    this.receiptSettingsStore
      .count()
      .then((numberOfReceipts: number) => {
        this.hasMultipleReceipts = numberOfReceipts > 1
      })
      .catch((error: $TSFixMe) => {
        $log.warn(
          'Failed to retrieve settings from receiptSettings store',
          error
        )
      })
    this.user = this.userManager.getLoggedInUser()
    if (this.trainingModeManager.trainingModeEnabled) {
      // Don't allow new customers to be created in Training Mode
      this.customerSource = this.customerSuggestions.getSource({
        readOnly: true,
      })
    } else {
      this.customerSource = this.customerSuggestions.getSource({
        create: ({ query }: $TSFixMe) => {
          return this.customerModals
            .createCustomer({ query })
            .then((customer: $TSFixMe) => this.addCustomer(customer))
        },
      })
    }
    this.customerErrorTemplate = this.customerSuggestions.customerErrorTemplate
    this.addCustomerPlaceholder =
      this.retailerSettingsManager.get().account_status === 'trialing'
        ? 'Add a customer to this sale'
        : 'Add a customer to email them a receipt'
    this.userManager.on('loggedInUserChange', this._onLoggedInUserChange, this)
    this.saleManager.on(
      'earlySubmitSaleComplete',
      this._onEarlySaleSubmitComplete,
      this
    )
    this._init()
    const escapeHandler = () => {
      if (this.trainingModeManager.trainingModeOnboardingState) {
        this.completeTutorial({ viaEscape: true })
      } else if (!this.isFirstTimeUploading() || isSynced(this.sale)) {
        this.closeSaleConfirmation({ source: 'escape' })
      }
    }
    // Create a shortcut to the escape key that closes the panel, via completeTutorial if training mode is enabled, or
    // closeSaleConfirmation if not.
    this.vdEscapeStack.register(escapeHandler)
    this.$scope.$on('$destroy', () => {
      this._unsubscribeStateChange()
      this.userManager.off('loggedInUserChange', this._onLoggedInUserChange)
      this.saleManager.off(
        'earlySubmitSaleComplete',
        this._onEarlySaleSubmitComplete
      )
      this.registerManager.off(
        'activeRegisterChange',
        this._setRegisterSettings
      )
      this.vdEscapeStack.deregister(escapeHandler)
    })
  }

  private _onLoggedInUserChange(event: $TSFixMe) {
    this.$scope.$applyAsync(() => {
      this.user = event.loggedInUser
    })
  }

  private _onEarlySaleSubmitComplete() {
    if (this.activePrinting && !this.isFinalisingSale) {
      if (this.activePrinting === 'gift') {
        this.saleManager.getReceiptData().then((data: ReceiptData) => {
          $rootScope.$emit(EVENT.PRINT, {
            type: 'styled-gift-receipt',
            data,
          })
        })
      } else {
        this.printReceipt(this.selectedReceiptId, this.isDefaultTemplate)
      }
      this.activePrinting = undefined
      this.selectedReceiptId = undefined
      this.isDefaultTemplate = undefined
    }
  }

  /**
   * Determine if we should display the sale note.
   *
   * @method shouldDisplaySaleNote
   *
   * @return {Boolean} True if the sale note should be shown, false otherwise
   */
  public shouldDisplaySaleNote() {
    return shouldDisplaySaleNote(
      this.registerManager.getActiveRegister(),
      this.sale
    )
  }

  public isPartialPaidSale() {
    return isPartialPaidSale(this.sale)
  }

  public isUnpaidSale() {
    return isUnpaidSale(this.sale)
  }

  public hasChange() {
    return hasChange(this.sale.change)
  }

  public getChangeAmountForDisplay() {
    return getChangeAmountForDisplay(this.sale)
  }

  public isSaleTotalZero() {
    return isSaleTotalZero(this.sale)
  }

  public isSaleTotalGreaterThanZero() {
    return isSaleTotalGreaterThanZero(this.sale)
  }

  public isSaleTotalLessThanZero() {
    return isSaleTotalLessThanZero(this.sale)
  }

  public hasOutstandingBalance() {
    return hasOutstandingBalance(this.sale)
  }

  public getOutstandingBalance() {
    return getOutstandingBalance(this.sale)
  }

  public isAccountBalancePaymentPaid() {
    return isAccountBalancePaymentPaid(this.sale.payAccountBalance)
  }

  public isMarkedAsUnfulfilled() {
    return isMarkedAsUnfulfilled(this.sale.status)
  }

  public hasNonZeroSumPayments() {
    return getPaymentsTotal(this.sale.payments) !== 0
  }

  public getPaymentsTotal() {
    return getPaymentsTotal(this.sale.payments)
  }

  public isValidCustomer() {
    return isValidCustomer(this.sale.customer)
  }

  public isValidCustomerWithEmail() {
    return Boolean(this.isValidCustomer() && this.sale.customer?.email)
  }

  public isValidCustomerWithLoyalty() {
    return Boolean(this.isValidCustomer() && this.sale.customer?.enable_loyalty)
  }

  /**
   * Expose register settings
   *
   * @private
   * @method _setRegisterSettings
   */
  private _setRegisterSettings() {
    const register = this.registerManager.getActiveRegister()
    this.displayEmailReceipt = register.email_receipt
    this.autoPrintEnabled = register.print_receipt
  }

  /**
   * Initialize the view and ensure the sale is in the proper state.
   *
   * @private
   * @method _init
   */
  private _init() {
    if (!this._whenInitialised) {
      this._whenInitialised = this.registerManager
        .waitForSelectedRegister()
        .then(() => {
          if (this.registerManager.isActiveRegisterOpen()) {
            return this.saleManager.init()
          } else {
            this._navigatingTo = [STATES.OPEN_REGISTER, {}]
            return $q.reject('Register is closed')
          }
        })
        .then(() => {
          // Only closed, on-account, layby and unfulfilled sales can be in the sale confirmation screen.
          if (
            !this.sale ||
            !(
              isCompleted(this.sale.status) ||
              isConfirmedReadyToComplete(this.sale) ||
              isQuote(this.sale.status) ||
              isMarkedAsUnfulfilled(this.sale.status) ||
              isLegacyServiceSale(this.sale.status)
            )
          ) {
            return $q.reject('Sale is not ready to be closed')
          }

          if (isMarkedAsUnfulfilled(this.sale.status)) {
            this.whenSaleCanBeFinalised = this.saleManager.setPickableState()
          }

          this.isCompleted = isCompleted(this.sale.status)
          this.isConfirmedReadyToComplete = isConfirmedReadyToComplete(
            this.sale
          )
          // When in training mode, update the confirmation screen
          if (this.trainingModeManager.trainingModeEnabled) {
            this.completeSaleLabel = 'Start a new sale in training mode'
            // Un-subscribe to prevent the double up of finaliseSale, because disabling training mode will trigger a state change.
            this._unsubscribeStateChange()
          } else if (isQuote(this.sale.status)) {
            this.completeSaleLabel = 'Complete quote'
          } else {
            this.completeSaleLabel = 'Complete Sale'
          }
          this._setRegisterSettings()
          this.registerManager.on(
            'activeRegisterChange',
            this._setRegisterSettings,
            this
          )
          this.loading = false
        })
        .catch((error: $TSFixMe) => {
          $log.warn(
            '[Sale Confirmation Ctrl] Redirecting due to error loading confirmation screen:',
            error
          )
          // If saleManager.init() fails or we have a sale that is not closed/confirmed then navigate away from the
          // sale confirmation screen.
          // Need to unsubscribe as otherwise stateChange listener will trigger. State change happens before destroy.
          this._unsubscribeStateChange()
          const navigateTo = this._navigatingTo || [STATES.SALE, {}]
          const [state, params] = navigateTo
          $state.go(state, params, { replace: true } as $TSFixMe)
          return $q.reject()
        })
    }
    return this._whenInitialised
  }

  /**
   * Completes tutorial by exiting training mode then finalising the sale (which removes the local sale and does not
   * post to the server). Then navigates back to current sale.
   *
   * @method completeTutorial
   *
   * @param [data] {Object}
   *
   *         @param [viaEscape] {Boolean}
   *                Identify if the escape key was used to trigger the closing of the sale confirmation view.
   */
  public completeTutorial(data?: any) {
    const currentTourType = this.trainingModeManager.currentTourType
    if (this.trainingModeManager.trainingModeEnabled) {
      this.trainingModeManager
        .exitTrainingMode()
        .then(() =>
          $state.go(STATES.SALE, {}, { location: 'replace', notify: true })
        )
        .then(() => {
          this.vdTracking.trackUserEvent({
            eventName: currentTourType!,
            metadata: {
              step: 'Completed Tutorial',
              triggeredBy: data && data.viaEscape ? 'Escape Key' : 'Button',
            },
          })
        })
    }
  }

  /**
   * Determine if a gift receipt can be printed. All sales with a 'closed' status (except 'return closed'), can
   * be printed.
   *
   * @method canPrintGiftReceipt
   *
   * @return {Boolean} True if a gift receipt can be printed for the current sale, false otherwise.
   */
  public canPrintGiftReceipt() {
    const giftReceiptsEnabled = this.vdFeatureManager.get('gift_receipts')
    return (
      giftReceiptsEnabled &&
      this.sale &&
      GIFT_RECEIPT_STATUSES.indexOf(this.sale.displayStatus!) > -1
    )
  }

  /**
   * Trigger printing of a receipt
   *
   * @method printReceipt
   * @param  {String} [receiptTemplateId] The ID of the receipt template to print.
   * @param  {Boolean} [defaultTemplate] Whether the default template was selected
   */
  public printReceipt(receiptTemplateId?: string, defaultTemplate?: boolean) {
    const trackingEvent = 'Print Receipt Clicked'
    if (this.trainingModeManager.trainingModeOnboardingState) {
      this.vdTracking.trackUserEvent({
        eventName: this.trainingModeManager.currentTourType!,
        metadata: {
          step: trackingEvent,
        },
      })
    } else {
      this.vdTracking.trackUserEvent({
        eventName: trackingEvent,
        metadata: {
          default_template: defaultTemplate !== false,
          sale_id: this.sale!.localId,
          sale_status: this.sale!.status,
          template_id: receiptTemplateId,
        },
      })
    }

    return this.saleManager.getReceiptData().then(async (data: ReceiptData) => {
      if (receiptTemplateId) {
        data.selectedReceiptId = receiptTemplateId
      }

      await print({
        type: 'receipt-settings-and-template',
        data,
      })
    })
  }

  /**
   * Check uploading and print receipt
   */

  public checkUploadAndPrint({
    receiptId,
    defaultTemplate,
    selectedPrintOption,
  }: CheckUploadAndPrintOptions) {
    if (this.isFirstTimeUploading()) {
      this.activePrinting = selectedPrintOption || 'print'
      this.selectedReceiptId = receiptId
      this.isDefaultTemplate = defaultTemplate
    } else {
      this.printReceipt()
    }
  }

  /**
   * Open the Receipt Selection popover
   * @method openReceiptSelectionPopover
   */
  public openReceiptSelectionPopover(event: $TSFixMe) {
    this.clickedPrintReceiptButton = angular.element(event.currentTarget)[0]
    this.clickedPrintReceiptButton.classList.add('vd-btn--active')
    this.vdPopover
      .open({
        trigger: event.currentTarget,
        template,
        controller: 'ReceiptSelectionPopoverController',
        hasPopoverList: true,
        direction: 'center',
        openAsModalAtViewport: 'xsmall',
      })
      .then((modal: $TSFixMe) => {
        modal.closed.then((data: $TSFixMe) => {
          if (data) {
            const { selectedReceipt, defaultTemplate } = data
            if (this.isFirstTimeUploading()) {
              this.checkUploadAndPrint({
                selectedPrintOption: 'other',
                receiptId: selectedReceipt.id,
                defaultTemplate,
              })
            } else {
              this.printReceipt(selectedReceipt.id, defaultTemplate)
            }
          }
          this.clickedPrintReceiptButton.classList.remove('vd-btn--active')
        })
      })
  }

  /**
   * Trigger printing of a gift receipt
   *
   * @method printGiftReceipt
   */
  public printGiftReceipt() {
    this.vdTracking.trackUserEvent({
      eventName: 'Gift Receipt Clicked',
    })

    if (this.isFirstTimeUploading()) {
      this.checkUploadAndPrint({
        selectedPrintOption: 'gift',
      })
    } else {
      this.saleManager.getReceiptData().then((data: $TSFixMe) => {
        $rootScope.$emit(EVENT.PRINT, {
          type: 'styled-gift-receipt',
          data,
        })
      })
    }
  }

  public getNegativeQuantity() {
    return getTotalQuantityByType({ lineItems: this.lineItems }, true)
  }

  public isExchange() {
    return saleIsExchange(selectCurrentSaleAsSale(this.$ngRedux.getState()))
  }

  public isFirstTimeUploading() {
    return this.sale.syncStatus === SaleSyncStatus.UPLOAD_FIRST_SUBMIT
  }

  public isPrintLoading(option?: PrintOptions) {
    if (option) {
      return this.isFirstTimeUploading() && this.activePrinting === option
    } else {
      return this.isFirstTimeUploading() && !!this.activePrinting
    }
  }

  /**
   * Saves the sale
   *
   * @method saveSale
   */
  public saveSale() {
    this.saleManager.saveSale()
    // Only track the first change to the sale note.
    if (!this.hasTrackedSaleNoteUpdated) {
      this.vdTracking.trackUserEvent({
        eventName: 'User Updated Sale Note on Confirmation Screen',
      })
      this.hasTrackedSaleNoteUpdated = true
    }
  }

  public editCustomerInformation() {
    this.customerModals.updateCustomer(this._getCustomer())
  }

  private _getCustomer() {
    return this.sale.customer
  }

  /**
   * Finalise the sale and dismiss the sale confirmation screen
   *
   * @method closeSaleConfirmation
   *
   * @param [targetRoute] {String}
   *        Route where to go after the sale has been posted
   *
   * @param [routeParams] {Object}
   *        Params to include with the route
   */
  public closeSaleConfirmation({
    targetRoute = STATES.SALE,
    routeParams,
    source,
  }: {
    targetRoute?: string
    routeParams?: $TSFixMe
    source: $TSFixMe
  }) {
    this._navigatingTo = [targetRoute, routeParams]
    this.isFinalisingSale = true
    return this._init()
      .then(() => {
        // Don't print when carrying out the on-boarding flow tour
        // and when using quotes service flow. The quote print will happen after the quote is created.
        if (
          this.autoPrintEnabled &&
          !this.trainingModeManager.trainingModeOnboardingState &&
          this.sale &&
          !isQuote(this.sale.status)
        ) {
          // Wait for the receipt data to be retrieved
          return this.printReceipt()
        } else {
          return $q.resolve()
        }
      })
      .then(() => {
        // Make sure the state listener is off otherwise will finalise twice
        this._unsubscribeStateChange()
        this.vdTracking.trackUserEvent({
          eventName: 'Close Sale Confirmation',
          metadata: {
            id: this.sale!.localId,
          },
        })
        // Track adding additional products to on-account sales
        if (
          this.sale!.status === SaleStatus.ACCOUNT_SALE ||
          this.sale!.status === SaleStatus.ACCOUNT_SALE_CLOSED
        ) {
          const lineItems = selectLineItems(this.$ngRedux.getState())
          let existingLineItemCount = 0
          lineItems.forEach(function (lineItem: $TSFixMe) {
            if (lineItem.id != null) {
              existingLineItemCount++
            }
          })
          if (
            existingLineItemCount > 0 &&
            existingLineItemCount < lineItems.length
          ) {
            this.vdTracking.trackUserEvent({
              eventName: 'Added additional product(s) to on-account sale',
            })
          }
        }
        // store the payments used type for performance tracking AFTER the sale is reset
        const tempPayments = this.sale.payments.map(p => {
          return {
            paymentTypeName: p.paymentType.name,
            paymentTypeId: p.paymentType.type_id,
            paymentDate: p.paymentDate,
            retailerPaymentTypeId: p.paymentType.id,
            salePaymentId: p.localId,
          }
        })
        this.$ngRedux.dispatch(setSaleCompletedBy(source))
        this.whenSaleCanBeFinalised
          .then(() =>
            isUploadedToBeCompleted(this.sale)
              ? this.saleManager.completeSale()
              : $q.when(this.saleManager.finaliseSale())
          )
          .then(() => {
            // Using `replace` prevents the back button of going back to confirmation screen
            const options = { location: 'replace' }
            // Clear the payment integration receipt message in case there are any left overs from previous transactions.
            this.paymentManager.paymentIntegrationReceiptMessage = null
            this.paymentManager.acceptedPaymentIntegrationReceiptMessages = []
            $state
              .go(targetRoute, routeParams, options)
              .then(() => {
                if (!this.trainingModeManager.trainingModeEnabled) {
                  // stop timing the full sale-tender process
                  this.vdPerformanceTracking.stop('sale-process', {
                    metadata: {
                      payments: tempPayments,
                    },
                  })
                }
                return $q.when(
                  this.userSwitchingService.switchUserIfNeededOnCompleteSale()
                )
              })
              .then(() =>
                $timeout(() =>
                  $rootScope.$emit('autocomplete:global-search:focus')
                )
              )
          })
      })
  }

  /**
   * Respond to a customer being added to the sale via the autocomplete input.
   *
   * @method onCustomerResultSelected
   *
   * @param {Suggestion} result
   *        The chosen Customer suggestion
   */
  public onCustomerResultSelected(result: $TSFixMe) {
    const { customer } = result
    this.addCustomer(customer)
  }

  /**
   * Add a customer to the sale via the "Add customer to sale" autocomplete.
   *
   * @method addCustomer
   */
  public addCustomer(customer: ApiCustomer) {
    this.saleManager.addCustomer(customer).then(() => {
      this.$ngRedux.dispatch(customerAddedOnConfirmation())
      this.saleManager.saveSale()
    })
  }

  /**
   * Wrapper for a helper function that determines if the sale is layby
   */
  public isLayby() {
    return isLayby(this.sale.status)
  }

  /**
   * Wrapper for a helper function that determines if the sale is on account
   */
  public isOnAccount() {
    return isOnAccount(this.sale.status)
  }

  /**
   * Determine if the local sale is confirmed state and completed
   *
   * @return True is the sale is in a confirmed and completed state, false otherwise
   */
  public isConfirmedCompleted() {
    return (
      this.sale.status && CONFIRMED_STATUSES_CLOSED.includes(this.sale.status)
    )
  }

  public isQuote() {
    return isQuote(this.sale.status)
  }

  public isLegacyServiceSale() {
    return isLegacyServiceSale(this.sale.status)
  }
}

/**
 * Controller for the sale confirmation screen
 *
 * @module webregister.controllers.SaleConfirmationCtrl
 */
angular
  .module('webregister.controllers')
  .controller('SaleConfirmationCtrl', SaleConfirmationCtrl)
